// frontend/src/utils/api.js

import axios from 'axios';

// ════════════════════════════════════════════════════════════════════════════
// BASE CONFIGURATION
// ════════════════════════════════════════════════════════════════════════════
const BACKEND_URL = process.env.REACT_APP_API_URL ||
    (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

const BASE_URL = `${BACKEND_URL}/api`;
export const UPLOADS_URL = BACKEND_URL || '';

// --- Helpers ---
export const getImageUrl = (path) => (!path ? '' : path.startsWith('http') ? path : `${UPLOADS_URL}${path}`);
export const getVideoUrl = (path) => (!path ? '' : path.startsWith('http') ? path : `${UPLOADS_URL}${path}`);
export const getPaymentScreenshotUrl = (path) => (!path ? '' : path.startsWith('http') ? path : `${UPLOADS_URL}${path}`);

const api = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' }
});

// ════════════════════════════════════════════════════════════════════════════
// REQUEST INTERCEPTOR
// ════════════════════════════════════════════════════════════════════════════
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('raajdhani_admin_token') ||
            localStorage.getItem('raajdhani_customer_token') ||
            localStorage.getItem('token');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('raajdhani_admin_token');
            localStorage.removeItem('raajdhani_customer_token');
            window.dispatchEvent(new Event('auth-logout'));
        }
        return Promise.reject(error);
    }
);

// ════════════════════════════════════════════════════════════════════════════
// API EXPORTS
// ════════════════════════════════════════════════════════════════════════════

// --- Announcements ---
export const getAnnouncements = () => api.get('/announcements');
export const getAllAnnouncements = () => api.get('/announcements/all');
export const createAnnouncement = (data) => api.post('/announcements', data);
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`);

// --- Products ---
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// --- Reviews (Fixes ProductDetail.jsx error) ---
export const addReview = (productId, data) => api.post(`/products/${productId}/reviews`, data);

// --- Orders (Fixes Checkout.jsx error) ---
export const placeOrder = (data) => api.post('/orders', data);
export const getOrders = (params) => api.get('/orders', { params });
export const getAllOrders = () => api.get('/orders/all');
export const updateOrderStatus = (id, updates) => api.patch(`/orders/${id}/status`, updates);

// --- Payment & Promo (Fixes Checkout.jsx error) ---
export const getPaymentInfo = () => api.get('/payment/info');
export const updatePaymentInfo = (data) => api.put('/payment/info', data);
// FIX: this previously called POST /payment/promo/validate, which does not
// exist on the backend (real route is GET /payment/promos/validate/:code) —
// every promo-code check at checkout was silently 404ing. Now matches.
export const validatePromo = (code) => api.get(`/payment/promos/validate/${encodeURIComponent(code)}`);
export const getPromos = () => api.get('/payment/promos');
export const createPromo = (data) => api.post('/payment/promos', data);
export const updatePromo = (id, data) => api.patch(`/payment/promos/${id}`, data);
export const deletePromo = (id) => api.delete(`/payment/promos/${id}`);

// --- Volumes & Sections ---
export const getVolumes = () => api.get('/volumes');
export const createVolume = (data) => api.post('/volumes', data);
export const getSections = () => api.get('/sections');
export const createSection = (data) => api.post('/sections', data);

// --- Auth ---
export const loginAdmin = (data) => api.post('/auth/login', data);
export const loginCustomer = (data) => api.post('/auth/customer-login', data);

export default api;